import type { Dispatch, RefObject, SetStateAction } from 'react';
import { useEffect } from 'react';
import { ListGroup, Nav, Spinner, Tab } from 'react-bootstrap';
import Notification from '@/components/navbar/top/notification/Notification';
import EmptyNotification from './EmptyNotification';
import type {
  DeletePrivateNotifBody,
  DeletePrivateNotifResponse,
  NotificationChannel,
  NotificationRow,
  ReadNotifBody,
  ReadNotifResponse
} from '@/app/common/api/notifications/types/inappApiTypes';

type NotificationsByChannel = Record<NotificationChannel, NotificationRow[]>;

type DeleteMutation = (arg: DeletePrivateNotifBody) => {
  unwrap: () => Promise<DeletePrivateNotifResponse>;
};

type ReadMutation = (arg: ReadNotifBody) => {
  unwrap: () => Promise<ReadNotifResponse>;
};

export interface NotificationListProps {
  notifications: NotificationsByChannel;
  hasMore: Record<NotificationChannel, boolean>;
  isFetching: Record<NotificationChannel, boolean>;
  fetchNextPage: (channel: NotificationChannel) => void | Promise<void>;
  activeTab: NotificationChannel;
  setActiveTab: (tab: NotificationChannel) => void;
  scrollContainerRef: RefObject<HTMLDivElement | null>;
  deleteNotification: DeleteMutation;
  readNotification: ReadMutation;
  setNotifications: Dispatch<SetStateAction<NotificationsByChannel>>;
  setCountUnreadByChannel: Dispatch<
    SetStateAction<Record<NotificationChannel, number>>
  >;
  successMessage: (msg: string) => void;
}

const NotificationList = ({
  notifications,
  hasMore,
  isFetching,
  fetchNextPage,
  activeTab,
  setActiveTab,
  scrollContainerRef,
  deleteNotification,
  readNotification,
  setNotifications,
  setCountUnreadByChannel,
  successMessage
}: NotificationListProps) => {
  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const handleScroll = () => {
      const bottom = el.scrollHeight - el.scrollTop <= el.clientHeight + 10;
      if (bottom && hasMore[activeTab] && !isFetching[activeTab]) {
        void fetchNextPage(activeTab);
      }
    };
    el.addEventListener('scroll', handleScroll);
    return () => el.removeEventListener('scroll', handleScroll);
  }, [activeTab, hasMore, isFetching, fetchNextPage, scrollContainerRef]);

  return (
    <ListGroup
      ref={scrollContainerRef}
      variant="flush"
      className="fw-normal fs--1 scrollbar"
      style={{ height: '19rem', overflowY: 'auto' }}
    >
      <Tab.Container defaultActiveKey="private">
        <div className="list-group-title p-0 px-3">
          <Nav
            onSelect={key => {
              if (key === 'private' || key === 'public') setActiveTab(key);
            }}
            variant="tabs"
          >
            <Nav.Item>
              <Nav.Link eventKey="private">خصوصی</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="public">عمومی</Nav.Link>
            </Nav.Item>
          </Nav>
        </div>
        <Tab.Content>
          {(['private', 'public'] as const).map(channel => (
            <Tab.Pane key={channel} eventKey={channel}>
              {notifications[channel].length > 0 ? (
                notifications[channel].map(notification => (
                  <ListGroup.Item key={notification.message_id}>
                    <Notification
                      {...notification}
                      deleteHandler={async () => {
                        deleteNotification({
                          message_id: notification.message_id,
                          channel,
                          delete_all: false
                        } as DeletePrivateNotifBody)
                          .unwrap()
                          .then(() => {
                            successMessage('حذف موفق');
                            setNotifications(prev => ({
                              ...prev,
                              [channel]: prev[channel].filter(
                                n => n.message_id !== notification.message_id
                              )
                            }));
                            if (!notification.is_read) {
                              setCountUnreadByChannel(prev => ({
                                ...prev,
                                [channel]: Math.max(prev[channel] - 1, 0)
                              }));
                            }
                          });
                      }}
                      readHandler={async () => {
                        if (notification.is_read) return;
                        readNotification({
                          message_id: notification.message_id,
                          channel,
                          read_all: false
                        })
                          .unwrap()
                          .then(() => {
                            setNotifications(prev => ({
                              ...prev,
                              [channel]: prev[channel].map(n =>
                                n.message_id === notification.message_id
                                  ? { ...n, is_read: true }
                                  : n
                              )
                            }));
                            setCountUnreadByChannel(prev => ({
                              ...prev,
                              [channel]: Math.max(prev[channel] - 1, 0)
                            }));
                          });
                      }}
                      channel={channel}
                      flush
                    />
                  </ListGroup.Item>
                ))
              ) : (
                <EmptyNotification />
              )}
              <div
                className={`text-center py-${!hasMore[channel] ? '0' : '3'}`}
              >
                &nbsp; {isFetching[channel] && <Spinner />} &nbsp;
              </div>
            </Tab.Pane>
          ))}
        </Tab.Content>
      </Tab.Container>
    </ListGroup>
  );
};

export default NotificationList;
